import React from 'react';
import { ArrowLeft } from 'lucide-react';

type PolicyType = 'terms' | 'privacy' | 'refund' | 'shipping';

interface PolicyPageProps {
  isOpen: boolean;
  onClose: () => void;
  policyType: PolicyType;
}

const POLICY_CONTENT: Record<PolicyType, { title: string; subtitle: string; content: string }> = {
  terms: {
    title: 'Terms & Conditions',
    subtitle: 'LEGAL_TERMS // V1.0',
    content: `
**Last Updated: April 2025**

Welcome to YUMECAV. By accessing or using our website (yumecav.in) and purchasing our products, you agree to comply with and be bound by these Terms and Conditions.

---

**1. GENERAL**
These terms govern your use of the YUMECAV website and all transactions made through it. By placing an order, you confirm that you are at least 18 years old or have parental/guardian consent.

**2. PRODUCTS**
All products listed on YUMECAV — including posters, stickers, and skins — are designed and curated in-house. Product images are representative; slight variations in color may occur due to screen differences.

**3. PRICING**
All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise. YUMECAV reserves the right to modify prices at any time without prior notice.

**4. ORDERS & PAYMENT**
Orders are confirmed once payment is successfully processed via Razorpay. We accept UPI, credit/debit cards, net banking, and wallets. If payment fails, the order will not be placed.

**5. CANCELLATION**
Once an order is confirmed and payment is received, it cannot be cancelled. In exceptional cases, please reach out to us at contact@yumecav.in within 2 hours of placing the order.

**6. INTELLECTUAL PROPERTY**
All designs, logos, images, and content on YUMECAV are the intellectual property of YUMECAV / The Ideal Firm. Reproduction, distribution, or unauthorized use is strictly prohibited.

**7. LIMITATION OF LIABILITY**
YUMECAV shall not be liable for any indirect, incidental, or consequential damages arising from the use of our website or products.

**8. GOVERNING LAW**
These terms shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Guwahati, Assam.

**9. CONTACT**
For any queries regarding these terms, reach out to:
📧 contact@yumecav.in
📞 7896409312
    `,
  },
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'DATA_PROTOCOL // V1.0',
    content: `
**Last Updated: April 2025**

YUMECAV ("we", "us", "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy outlines how we collect, use, and safeguard your data.

---

**1. INFORMATION WE COLLECT**
We collect the following information when you use our website or place an order:
- **Personal Information:** Name, email address, phone number, shipping address, and PIN code.
- **Payment Information:** Processed securely through Razorpay. We do not store your card details.
- **Account Information:** Firebase authentication data (email/phone used for login).
- **Usage Data:** Pages visited, time spent, device type, and browser information (via analytics).

**2. HOW WE USE YOUR INFORMATION**
- To process and fulfill your orders
- To communicate order updates and shipping details
- To improve our website and user experience
- To send promotional content (only with your consent)
- To prevent fraud and ensure security

**3. DATA SHARING**
We do not sell your personal data. We share information only with:
- **Razorpay** — for payment processing
- **Shiprocket** — for order fulfillment and shipping
- **Firebase** — for authentication services
- **SMTP services** — for transactional emails

**4. DATA SECURITY**
We implement industry-standard security measures including HTTPS encryption, secure API endpoints, and Firebase Auth token verification.

**5. COOKIES**
Our website may use cookies to enhance your browsing experience. You can disable cookies in your browser settings.

**6. YOUR RIGHTS**
You have the right to:
- Access, correct, or delete your personal data
- Opt out of marketing communications
- Request data portability

**7. CONTACT**
For privacy-related queries:
📧 contact@yumecav.in
📞 7896409312
📍 Guwahati, Assam, India
    `,
  },
  refund: {
    title: 'Refund Policy',
    subtitle: 'REFUND_PROTOCOL // V1.0',
    content: `
**Last Updated: April 2025**

At YUMECAV, we want you to love what you receive. Please read our refund policy carefully before placing an order.

---

**1. ELIGIBILITY FOR REFUND**
Refunds are applicable in the following cases:
- **Damaged Product:** If the product arrives damaged, torn, or materially defective.
- **Wrong Product:** If you receive a product different from what you ordered.
- **Missing Items:** If items from your order are missing.

**2. NON-REFUNDABLE CASES**
Refunds will NOT be issued for:
- Change of mind after purchase
- Minor color variations between screen display and physical product
- Products damaged due to mishandling after delivery
- Sale or discounted items (unless defective)

**3. HOW TO REQUEST A REFUND**
To initiate a refund:
1. Email us at **contact@yumecav.in** within **48 hours** of delivery.
2. Include your **Order ID** (YMC-XXXXX), clear **photographs** of the issue, and a brief description.
3. Our team will review your request within **3-5 business days**.

**4. REFUND PROCESS**
- Approved refunds will be processed to the original payment method within **7-10 business days**.
- In some cases, we may offer a replacement instead of a refund.
- Shipping charges are non-refundable unless the issue is on our end.

**5. EXCHANGES**
We currently do not offer exchanges. If your product is eligible for a refund, you may place a new order for the desired item.

**6. CONTACT**
For refund-related queries:
📧 contact@yumecav.in
📞 7896409312
    `,
  },
  shipping: {
    title: 'Shipping Policy',
    subtitle: 'LOGISTICS_PROTOCOL // V1.0',
    content: `
**Last Updated: April 2025**

YUMECAV ships across India. Here's everything you need to know about our shipping process.

---

**1. SHIPPING CHARGES**
- **Orders ₹399 and above:** FREE shipping across India 🎉
- **Orders below ₹399:** A flat shipping fee of ₹69 applies.

**2. PROCESSING TIME**
- Orders are processed within **1-3 business days** after payment confirmation.
- You will receive an email with tracking details once your order is shipped.

**3. DELIVERY TIME**
- **Metro cities:** 3-5 business days
- **Tier 2/3 cities:** 5-8 business days
- **Remote areas:** 7-12 business days

Delivery times are estimates and may vary due to courier delays, weather conditions, or unforeseen circumstances.

**4. SHIPPING PARTNER**
We use **Shiprocket** and its network of courier partners to deliver your orders safely and on time.

**5. ORDER TRACKING**
Once shipped, you will receive:
- An email with your tracking number and courier details
- Real-time tracking available in the **"My Orders"** section on our website

**6. UNDELIVERABLE ORDERS**
If a shipment is returned to us due to:
- Incorrect address
- Recipient unavailable after multiple attempts
- Refused delivery

We will contact you to arrange re-shipment (additional shipping charges may apply).

**7. DAMAGED IN TRANSIT**
If your package arrives damaged, please refer to our **Refund Policy** for next steps.

**8. CONTACT**
For shipping-related queries:
📧 contact@yumecav.in
📞 7896409312
📍 Guwahati, Assam, India
    `,
  },
};

const PolicyPage: React.FC<PolicyPageProps> = ({ isOpen, onClose, policyType }) => {
  if (!isOpen) return null;

  const policy = POLICY_CONTENT[policyType];

  // Simple markdown-to-JSX renderer for bold, headings, horizontal rules, lists, and emojis
  const renderContent = (text: string) => {
    const lines = text.trim().split('\n');
    const elements: React.ReactNode[] = [];

    lines.forEach((line, idx) => {
      const trimmed = line.trim();

      if (trimmed === '') {
        elements.push(<div key={idx} className="h-3" />);
      } else if (trimmed === '---') {
        elements.push(<hr key={idx} className="border-white/10 my-6" />);
      } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        // Bold heading-style line
        elements.push(
          <h3 key={idx} className="text-sm font-black uppercase tracking-wider text-white mt-6 mb-2">
            {trimmed.replace(/\*\*/g, '')}
          </h3>
        );
      } else if (trimmed.startsWith('- **')) {
        // Bold list item
        const match = trimmed.match(/^- \*\*(.+?)\*\*(.*)$/);
        if (match) {
          elements.push(
            <div key={idx} className="flex items-start gap-2 ml-2 my-1">
              <span className="text-amber-500 mt-1 text-[8px]">◆</span>
              <p className="text-xs text-neutral-300 leading-relaxed">
                <strong className="text-white font-bold">{match[1]}</strong>{match[2]}
              </p>
            </div>
          );
        }
      } else if (trimmed.startsWith('- ')) {
        elements.push(
          <div key={idx} className="flex items-start gap-2 ml-2 my-1">
            <span className="text-amber-500 mt-1 text-[8px]">◆</span>
            <p className="text-xs text-neutral-300 leading-relaxed">{renderInlineBold(trimmed.slice(2))}</p>
          </div>
        );
      } else if (/^\d+\./.test(trimmed)) {
        // Numbered list
        const num = trimmed.match(/^(\d+)\.\s*(.*)/);
        if (num) {
          elements.push(
            <div key={idx} className="flex items-start gap-3 ml-2 my-1.5">
              <span className="text-amber-500 font-mono text-[10px] font-bold mt-0.5 min-w-[16px]">{num[1]}.</span>
              <p className="text-xs text-neutral-300 leading-relaxed">{renderInlineBold(num[2])}</p>
            </div>
          );
        }
      } else {
        elements.push(
          <p key={idx} className="text-xs text-neutral-400 leading-relaxed my-1">
            {renderInlineBold(trimmed)}
          </p>
        );
      }
    });

    return elements;
  };

  // Render inline **bold** segments
  const renderInlineBold = (text: string): React.ReactNode => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-white font-bold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black overflow-y-auto animate-fade-in no-scrollbar">
      <div className="max-w-3xl mx-auto py-12 px-6">
        {/* Header */}
        <header className="flex justify-between items-center mb-12">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="text-xs font-mono text-neutral-600 uppercase tracking-widest">
            {policy.subtitle}
          </div>
        </header>

        {/* Title */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter mb-3">
            {policy.title} <span className="text-amber-500">.</span>
          </h1>
          <div className="h-1 w-16 bg-amber-500" />
        </div>

        {/* Content */}
        <div className="bg-neutral-900/50 rounded-2xl border border-white/5 p-6 md:p-10">
          {renderContent(policy.content)}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-[10px] text-neutral-600 font-mono uppercase tracking-widest">
            &copy; {new Date().getFullYear()} YUMECAV. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PolicyPage;
